import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
  North,
  South
} from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme
} from '@mui/material';
import BakeasyLinkButton from '../buttons/BakeasyLinkButton';
import { t } from 'i18next';
import React from 'react';
import BakeasyCheckbox from '../common/BakeasyCheckbox';
import * as TextField from '../textfields/BakeasyTextFields';
import _ from 'lodash';
import { BLUE_GRAY_50, GRAY_200, GRAY_800, GREEN_50, RED_50 } from '../../constants/Colors';
import { storeState } from '../../redux_store/slices/storeSlice';
import { useAppSelector } from '../../redux_store/reduxStore';
import { getCurrency } from '../../utils/commonUtils';

interface Data {
  id: number | string;
  name: string;
  emailId: string;
  mobile: string;
  status: JSX.Element;
  actions: JSX.Element[];
  storeId: string;
  storeName: string;
  supplierName: string;
  addressCity: string;
  tag: string;
  price: string;
  category: string;
  subCategory: string;
  orderId: string;
  totalPrice: string;
  expectedDate: string;
  deliveryDate: string;
  productName: string;
  contact: string;
  recipeBuilder: string;
  duplicate: string;
  dateCreated: string;
  lastOrderDate: string;
  totalOrders: string;
  totalOrderValue: string;
  ingredientName: string;
  measureType: string;
  units: string;
  nutritionName: string;
  dailyvalue: string;
  dateTime: string;
  From: string;
  To: string;
  recsent: string;
  Content: string;
  Priority: string;
  subject: string;
  lastAccess: string;
  stores: string;
  products: string;
  edit: string;
  supplierId: string;
  shippingId: string;
  shippingDate: string;
  dueDate: string;
}

export interface HeadCell {
  disablePadding?: boolean;
  id: keyof Data | string;
  label: string;
  numeric?: boolean | false;
  isChip?: boolean | false;
  sort?: boolean | false;
  align?: string | 'left';
  width?: string;
  disabled?: boolean | false;
}

interface BakeasyTableMainProps {
  headers?: any[];
  headCells?: any[];
  tableData: any;
  rowProps?: number;
  pageNo?: number;
  navigate?: any;
  count?: any;
  refreshPage?: any;
  refreshPageData?: any;
  api?: string;
  sortBy?: any;
  sortDirection?: any;
  search?: any;
  handleCheckChanged?: any;
  handleQuantityChanged?: any;
  filter?: any;
}

const BakeasyTable = React.forwardRef(
  (
    props: BakeasyTableMainProps,
    ref: React.ForwardedRef<{
      handleChangePage: (event: unknown, newPage: number) => void;
    }>
  ) => {
    const {
      headers,
      tableData,
      count,
      refreshPageData,
      pageNo,
      handleCheckChanged,
      handleQuantityChanged,
      search,
      filter
    } = props;
    const [order, setOrder] = React.useState<number>(filter?.sortDirection ?? -1);
    const [orderBy, setOrderBy] = React.useState<string>(filter?.sortBy ?? '');
    const [tableDataList, setTableDataList] = React.useState<any>([]);
    const [page, setPage] = React.useState(pageNo ?? 0);
    const [mouseOver, setMouseOver] = React.useState<string>('');

    let _storeState = useAppSelector(storeState);

    React.useImperativeHandle(ref, () => ({
      handleChangePage
    }));

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
      refreshPageData(newPage, search, filter);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setPage(0);
      let newFilter: any = {
        ...filter,
        limit: Number(event.target.value)
      };

      refreshPageData(0, search, newFilter);
    };

    React.useEffect(() => {
      setTableDataList(tableData);
    }, [tableData]);

    function handleCheckBox(row: any, checked: boolean) {
      let newRow = row;
      if (checked) {
        newRow = tableDataList.find((item: any) => item.productId === row.productId);
      }
      newRow.productChecked = checked;
      handleCheckChanged(newRow || row, checked);
    }

    const handleQuantityChange = (row: any, value: any) => {
      let newRow = tableDataList.find((item: any) => item.productId === row.productId);
      const newValue = Number(value);
      newRow.quantity = newValue;
      handleQuantityChanged(newRow || row, newValue);
    };

    const ArrowIcon = (property: any) => {
      let northColor = 'gray',
        southColor = 'gray';

      if (orderBy === property) {
        if (order === -1) {
          northColor = 'black';
          southColor = 'gray';
        } else if (order === 1) {
          northColor = 'gray';
          southColor = 'black';
        }
      }

      return (
        <Grid
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'Center',
            alignItems: 'Center',
            margin: 0
          }}
        >
          <IconButton size="small" sx={{ color: orderBy === property ? 'black' : 'gray' }}>
            {orderBy === property && order === 1 && <North fontSize="inherit" />}
            {orderBy === property && order === -1 && <South fontSize="inherit" />}
            {mouseOver === property && mouseOver !== orderBy && <North fontSize="inherit" />}
          </IconButton>
        </Grid>
      );
    };

    function CustomTableCell(id: any, row: any) {
      switch (id) {
        case 'categoryName':
          if (row['onCategoryNameClick']) {
            return <BakeasyLinkButton label={row[id]} onClick={row['onCategoryNameClick']} />;
          } else {
            return row[id];
          }
        case 'subCategoryName':
          if (row['onSubCategoryNameClick']) {
            return <BakeasyLinkButton label={row[id]} onClick={row['onSubCategoryNameClick']} />;
          } else {
            return row[id];
          }
        case 'productName':
          if (row['onProductNameClick']) {
            return <BakeasyLinkButton label={row[id]} onClick={row['onProductNameClick']} />;
          } else {
            return row[id];
          }
        case 'name':
          return <BakeasyLinkButton label={row[id]} onClick={row['onNameClick']} />;
        case 'storeName':
          if (row['onStoreNameClick']) {
            return <BakeasyLinkButton label={row[id]} onClick={row['onStoreNameClick']} />;
          } else {
            return row[id];
          }
        case 'supplierName':
          return <BakeasyLinkButton label={row[id]} onClick={row['onSupplierNameClick']} />;
        case 'contact':
          return (
            <BakeasyLinkButton
              label={row[id]}
              style={{ textAlign: 'left' }}
              onClick={row['onContactClick']}
            />
          );
        case 'products':
          return <BakeasyLinkButton label={row[id]} onClick={row['onProductsClick']} />;
        case 'orderId':
          return <BakeasyLinkButton label={row[id]} onClick={row['onOrderIdClick']} />;
        case 'invoiceId':
          return <BakeasyLinkButton label={row[id]} onClick={row['onInvoiceIdClick']} />;
        case 'shippingId':
          return <BakeasyLinkButton label={row[id]} onClick={row['onShippingIdClick']} />;
        case 'storeContact':
          return <BakeasyLinkButton label={row[id]} onClick={row['onStoreContactClick']} />;
        case 'AssignedProducts':
          return <BakeasyLinkButton label={row[id]} onClick={row['onAssignedProductsClick']} />;
        case 'categoryCheck':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              onChange={function(event: React.ChangeEvent<HTMLInputElement>): void {
                handleCheckChanged(row);
              }}
            />
          );
        case 'messageDateTime':
          const dateTime = row[id];
          const date = dateTime.substring(0, 10);
          const time = dateTime.substring(11);
          return (
            <div>
              <div> {date}</div>
              <div style={{ color: GRAY_800, fontSize: '0.8rem' }}>{time}</div>
            </div>
          );
        case 'productChecked':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              onChange={function(event: React.ChangeEvent<HTMLInputElement>): void {
                handleCheckBox(row, event.target.checked);
              }}
            />
          );
        case 'quantity':
          return (
            // <TextField.Quantity
            //   value={row[id]}
            //   onChange={(event: any) => {
            //     handleQuantityChange(row, event.target.value);
            //   }}
            // />

            <TextField.Quantity
              // value={row[id]?.startsWith('0') ? row[id]?.slice(1) : row[id]}
              value={
                String(row[id]).length > 0
                  ? String(row[id]).startsWith('0') && String(row[id]) !== '0'
                    ? String(row[id]).slice(1)
                    : String(row[id])
                  : '0'
              }
              onChange={(event: any) => {
                const newValue = event.target.value;
                const formattedValue =
                  newValue.length > 0
                    ? newValue.startsWith('0') && newValue !== '0'
                      ? newValue.slice(1)
                      : newValue
                    : '0';
                handleQuantityChange(row, formattedValue);
              }}
            />
          );
        case 'subject':
          return (
            <div
              style={{
                //whiteSpace: 'normal',
                wordWrap: 'break-word'
                //overflow: 'hidden' // Optional: Hide overflowing text
              }}
            >
              {row[id]}
            </div>
          );
        default:
          //return row[id];
          return (
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {row[id]}
            </div>
          );
      }
    }

    function getHeaderLabel(header: any) {
      switch (header?.id) {
        case 'storeCost':
        case 'ordervalue':
        case 'totalPrice':
        case 'price':
          return t(header?.label) + getCurrency(_storeState?.value);
        default:
          return t(header?.label);
      }
    }
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Paper sx={{ width: '100%' }}>
          <TableContainer style={{ overflowX: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 500 }} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headers?.map((header, index) => (
                    <TableCell
                      style={{
                        padding: 10,
                        backgroundColor: GRAY_200,
                        textAlign: header.align,
                        whiteSpace: 'nowrap',
                        width: header?.width ?? 'auto',
                        maxWidth: header?.width ?? 'auto',
                        minWidth: header?.width ?? 'auto',
                        textOverflow: 'ellipsis',
                        paddingRight: header?.align === 'right' ? 20 : 0
                      }}
                      key={index}
                    >
                      {!header.sort ? (
                        <Typography style={{ fontWeight: 'bold' }}>
                          {getHeaderLabel(header)}
                        </Typography>
                      ) : (
                        <TableSortLabel
                          IconComponent={() => ArrowIcon(header?.id)}
                          onMouseOver={(event: any) => {
                            setMouseOver(header?.id);
                          }}
                          onMouseOut={(event: any) => {
                            setMouseOver('');
                          }}
                          onClick={(event: any) => {
                            let _order = order === 1 && orderBy === header?.id ? -1 : 1;
                            let _orderBy =
                              order === -1 && orderBy === header?.id ? 'createdDate' : header?.id;

                            if (_orderBy === 'createdDate' && orderBy === header?.id) {
                              _order = order === 1 ? -1 : 1;
                            }

                            let newFilter: any = {
                              ...filter,
                              sortBy: _orderBy,
                              sortDirection: _order
                            };
                            setOrder(_order);
                            setOrderBy(_orderBy);
                            setPage(0);
                            refreshPageData(0, search, newFilter);
                          }}
                        >
                          <Typography style={{ fontWeight: 'bold' }}>{t(header.label)}</Typography>
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableDataList?.map((row: any, index: any) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: (() => {
                        if (row?.highPriority === true) {
                          return RED_50;
                        } else if (row?.status?.props?.label === 'New') {
                          return GREEN_50;
                        } else if (row?.status?.props?.label === 'Split') {
                          return BLUE_GRAY_50;
                        } else if (
                          row?.priority?.props?.label === 'urgent' &&
                          row?.status === 'new'
                        ) {
                          return RED_50;
                        } else if (row?.status === 'new') {
                          return GREEN_50;
                        } else {
                          return 'auto';
                        }
                      })()
                    }}
                  >
                    {headers?.map((header, headerIndex) => (
                      <TableCell
                        style={{
                          padding: 10,
                          textAlign: header.align,
                          overflow: 'hidden',
                          width: header?.width ?? 'auto',
                          maxWidth: header?.width ?? 'auto',
                          minWidth: header?.width ?? 'auto',
                          textOverflow: 'ellipsis',
                          paddingRight: header?.align === 'right' ? 20 : 0
                        }}
                        width={header.width ? header.width : 'auto'}
                        key={headerIndex}
                        align={header.align}
                      >
                        {CustomTableCell(header.id, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={count}
            rowsPerPage={filter?.limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            labelRowsPerPage={t('ROWS_PER_PAGE')}
          />
        </Paper>
      </Box>
    );
  }
);

export default BakeasyTable;

export interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

export function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}
